/**
 * Adapter Tela 2 Acompanhamento Silencioso — wire (ConversationDetail +
 * MessageItem[] + composer-state + quality-indicators) → view-models do espelho.
 * Funções puras (espelha tela8-adapter).
 *
 * Divergências mockup ↔ contrato real (spec §5), mapeadas honestas:
 *   - operationMode real é `H_a|H_b|H_c|H_d` (uppercase) → ModeBadge só conhece
 *     h_a/h_b/h_c; H_d mapeia para h_a (delegação manual = humano dirige).
 *   - bond/subject são stub vazio no detail → bondLabel degrada para null.
 *   - quality: sentiment ENUM (não score /10); adherence/usage FRAÇÃO 0..1 (não
 *     breakdown accepted/edited/rejected — backend não dá); responseTime em seg.
 *   - composer só é "digitando" quando há userId + texto não-vazio.
 */
import { channelLabel, initialsOf, relativeTime, statusLabel } from '../../../conversations/format';

import type { ConversationDetail, ConversationListItem, MessageItem } from '../../../../lib/types';
import type { ConversationComposerStateWire, QualityIndicators } from '../../types';
import type {
  ConversationMirrorVM,
  MirrorMessageVM,
  MirrorQualityVM,
  PanelQualityVM,
  ParallelConvVM,
  PresenceState,
} from '../../view-models';
import type { OperationModeKind } from '@lume/shared';

const PLACEHOLDER = '—';

/** Mapeia o modo de operação real (uppercase, 4 valores) p/ o ModeBadge (3 valores). */
function adaptMode(mode: OperationModeKind | undefined): 'h_a' | 'h_b' | 'h_c' | null {
  switch (mode) {
    case 'H_a':
    case 'H_d': // delegação manual → humano dirige (ModeBadge não tem h_d)
      return 'h_a';
    case 'H_b':
      return 'h_b';
    case 'H_c':
      return 'h_c';
    default:
      return null;
  }
}

/** Sentimento enum → label PT + tom (sem nº fake /10 — o backend não dá score). */
export function sentimentMeta(s: QualityIndicators['sentiment']): {
  label: string;
  tone: 'success' | 'neutral' | 'warning' | 'danger';
} {
  switch (s) {
    case 'positive':
      return { label: 'Positivo', tone: 'success' };
    case 'neutral':
      return { label: 'Neutro', tone: 'neutral' };
    case 'negative':
      return { label: 'Negativo', tone: 'warning' };
    case 'hostile':
      return { label: 'Hostil', tone: 'danger' };
    default:
      return { label: 'Neutro', tone: 'neutral' };
  }
}

/** Uma MessageItem → balão do espelho (read-only). */
export function adaptMirrorMessage(m: MessageItem): MirrorMessageVM {
  return {
    id: m.id,
    who: m.origin === 'customer' ? 'customer' : 'attendant',
    text: m.content,
    // A UI já posiciona o tempo — remove o prefixo "há " do helper compartilhado.
    atLabel: relativeTime(m.sentAt).replace(/^há /, ''),
  };
}

export function adaptQualitySummary(q: QualityIndicators): MirrorQualityVM {
  const sm = sentimentMeta(q.sentiment);
  const hasSample = q.responseTime.sampleSize > 0;
  return {
    sentimentLabel: sm.label,
    sentimentTone: sm.tone,
    adherencePct: Math.round(q.playbookAdherence * 100),
    aiUsagePct: Math.round(q.aiSuggestionUsage * 100),
    avgResponseLabel: hasSample ? `${Math.round(q.responseTime.avgSec).toString()}s` : PLACEHOLDER,
  };
}

/** "Ns" para segundos arredondados; "—" quando não há amostra. */
function secLabel(seconds: number, hasSample: boolean): string {
  return hasSample ? `${Math.round(seconds).toString()}s` : PLACEHOLDER;
}

/**
 * Display COMPLETO dos indicadores de qualidade (painel de ferramentas).
 * Divergência honesta (spec §5): sentiment enum→label+tom (sem nº /10);
 * adherence/usage fração→% (sem breakdown accepted/edited/rejected — o backend
 * não dá); responseTime → avg + p50/p95 em segundos; '—' quando sampleSize=0.
 */
export function adaptPanelQuality(q: QualityIndicators): PanelQualityVM {
  const sm = sentimentMeta(q.sentiment);
  const hasSample = q.responseTime.sampleSize > 0;
  return {
    sentimentLabel: sm.label,
    sentimentTone: sm.tone,
    adherencePct: Math.round(q.playbookAdherence * 100),
    aiUsagePct: Math.round(q.aiSuggestionUsage * 100),
    avgResponseLabel: secLabel(q.responseTime.avgSec, hasSample),
    p50Label: secLabel(q.responseTime.p50Sec, hasSample),
    p95Label: secLabel(q.responseTime.p95Sec, hasSample),
    sampleSize: q.responseTime.sampleSize,
  };
}

/** Conversa paralela do atendente (D10) → linha do painel. Degrada honesto. */
export function adaptParallelConv(c: ConversationListItem): ParallelConvVM {
  return {
    id: c.id,
    customerName: c.person?.displayName ?? PLACEHOLDER,
    channelLabel: channelLabel(c.channel),
    statusLabel: statusLabel(c.status),
  };
}

export interface AdaptMirrorParams {
  conversation: ConversationDetail;
  messages: MessageItem[];
  composer: ConversationComposerStateWire | undefined;
  /** Presence do atendente atribuído (resolvido na página via usePresence). */
  presenceFor: (userId: string) => PresenceState;
  /** Resolve userId → nome (presence). Degrada para o próprio id. */
  resolveName: (userId: string) => string;
}

export function adaptMirror(params: AdaptMirrorParams): ConversationMirrorVM {
  const { conversation: c, messages, composer, presenceFor, resolveName } = params;

  const customerName = c.person?.displayName ?? PLACEHOLDER;
  const attendantId = c.assignedToUser?.id ?? null;
  const attendantName = c.assignedToUser?.name ?? PLACEHOLDER;

  const composerText = composer?.currentText ?? '';
  const composerUserId = composer?.userId ?? null;
  const composing = composerUserId !== null && composerText.trim().length > 0;
  const composerName = composerUserId !== null ? resolveName(composerUserId) : attendantName;

  return {
    header: {
      customerName,
      customerInitials: c.person !== null ? initialsOf(customerName) : '?',
      channelIdentifier: c.identity?.channelIdentifier ?? null,
      channelLabel: channelLabel(c.channel),
      classificationLabel:
        c.classificationLevel !== null ? `N${c.classificationLevel.toString()}` : null,
      mode: adaptMode(c.operationMode?.current),
      attendantName,
      attendantInitials: c.assignedToUser !== null ? initialsOf(attendantName) : '?',
      attendantPresence: attendantId !== null ? presenceFor(attendantId) : 'offline',
      // Bond/subject são stub vazio no detail (spec §5) — degrada honesto.
      bondLabel: null,
    },
    messages: messages.map(adaptMirrorMessage),
    composer: composing
      ? {
          attendantName: composerName,
          attendantInitials: initialsOf(composerName),
          text: composerText,
        }
      : null,
  };
}
